/** Builds Auth0 appState return path payload from pathname/search. */
function buildAuth0ReturnTo(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

const RETURN_TO_STORAGE_KEY = "remerge.auth.returnTo";

/**
 * Persists the post-login return path
 *
 * auth0.logout() round-trips through the Auth0 domain and returns to the bare
 * origin, so the deep link the user was on survives only if stashed here
 * first. sessionStorage is per-tab and survives cross-origin navigation, the
 * same semantics the Auth0 SDK relies on for its own transaction storage.
 */
export function stashReturnTo(pathname: string, search: string): void {
  writeStash(buildAuth0ReturnTo(pathname, search));
}

export function takeStashedReturnTo(): string | null {
  try {
    const returnTo = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    if (returnTo === null) return null;
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    return returnTo.startsWith("/") ? returnTo : null;
  } catch {
    return null;
  }
}

/**
 * Builds the loginWithRedirect appState, preferring a stashed return path
 * (left by a forced logout) over the current location. Re-stashes the chosen
 * path so it also survives a lost Auth0 transaction (e.g. the callback
 * exchange failing mid-round-trip); a successful restore consumes it in
 * onAuth0Redirect.
 */
export function buildRedirectState(pathname: string, search: string): { returnTo: string } {
  const stashedReturnTo = takeStashedReturnTo();
  const returnTo = stashedReturnTo === null ? buildAuth0ReturnTo(pathname, search) : stashedReturnTo;
  writeStash(returnTo);
  return { returnTo };
}

function writeStash(returnTo: string): void {
  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, returnTo);
  } catch {
    // sessionStorage unavailable (private mode, storage disabled): the deep
    // link restore degrades gracefully to the pre-stash behavior.
  }
}

type RestoreAuth0CallbackPathParams = {
  appState?: { returnTo?: string };
  pathname: string;
  replaceState: (url: string) => void;
};

/** Restores callback path after Auth0 redirect. */
export function restoreAuth0CallbackPath({ appState, pathname, replaceState }: RestoreAuth0CallbackPathParams): void {
  const returnTo = appState?.returnTo;

  if (returnTo && returnTo.startsWith("/")) {
    replaceState(returnTo);
    return;
  }

  replaceState(pathname);
}

/**
 * Auth0 onRedirectCallback that restores the pre-login path/query using
 * window.history.replaceState. Pass directly to SharedAuth0Provider.
 * Always consumes the stashed return path: it backfills a missing appState
 * and must not leak into a later, unrelated login.
 */
export function onAuth0Redirect(appState?: { returnTo?: string }): void {
  const stashedReturnTo = takeStashedReturnTo();
  const returnTo = appState?.returnTo || stashedReturnTo;

  restoreAuth0CallbackPath({
    appState: returnTo ? { returnTo } : undefined,
    pathname: window.location.pathname,

    replaceState: url => {
      window.history.replaceState({}, document.title, url);
      window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
    },
  });
}
